import { ComposerBannerStackAlert } from "./ComposerBannerStackAlert";
// Ported from pingdotgg/t3code at d29c56a5c404cb0f58d3b2ac41762fa0d0ac28d4.
// MIT license retained in LICENSE.
import "./composer-notices.css";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
  type HTMLAttributes,
} from "react";

import { cn } from "~/lib/utils";
import { ComposerBanner, type ComposerBannerVariant } from "./ComposerBanner";

export interface ComposerBannerStackItem {
  readonly id: string;
  readonly variant: ComposerBannerVariant;
  readonly priority?: "urgent" | "activity" | "notice";
  readonly icon: ReactNode;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
  readonly dismissLabel?: string;
  readonly onDismiss?: () => void;
}

type ComposerBannerStackEntry = ComposerBannerStackItem;

function bannerPriority(item: ComposerBannerStackEntry) {
  if (item.priority === "activity") {
    return 0;
  }
  if (item.priority === "urgent" || item.variant === "error" || item.variant === "warning") {
    return 1;
  }
  return 2;
}

interface ComposerBannerStackProps {
  readonly className?: string;
  readonly testId?: string;
  readonly items: ReadonlyArray<ComposerBannerStackEntry>;
}

export function ComposerBannerStack({ className, items, testId }: ComposerBannerStackProps) {
  const [stackExpanded, setStackExpanded] = useState(false);
  const noticesRef = useRef<HTMLDivElement>(null);
  const peekRef = useRef<HTMLButtonElement>(null);
  const expandedItemsRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<"peek" | "notice" | null>(null);
  const expandedItemsId = useId();
  useEffect(() => {
    if (items.length < 2) setStackExpanded(false);
  }, [items.length]);

  useNoticeFocus(stackExpanded, pendingFocusRef, expandedItemsRef, peekRef);

  const noticeEvents = useNoticeEvents(stackExpanded, setStackExpanded, peekRef, pendingFocusRef);

  // Activity stays attached. Urgency and severity only order the notices behind it.
  const orderedItems = items.toSorted((a, b) => bannerPriority(a) - bannerPriority(b));
  const frontItem = orderedItems[0];
  if (!frontItem) {
    return null;
  }
  const stackedItems = orderedItems.slice(1);
  const firstStackedItem = stackedItems[0];

  return (
    <ComposerBanner.Attachment
      className={cn("composer-notice-stack", className)}
      data-testid={testId}
      data-composer-banner-drawer="true"
      data-chat-composer-collapsed-controls="true"
    >
      <div
        className="relative flex flex-col-reverse data-[expanded=true]:z-50"
        data-expanded={stackExpanded}
      >
        <div
          key={frontItem.id}
          className="relative z-10"
          onPointerDownCapture={() => {
            setStackExpanded(false);
            blurNoticeFocus(noticesRef.current);
          }}
        >
          <ComposerBannerStackAlert
            item={frontItem}
            attached
            onDismissRequest={(control) => requestNoticeDismissal(frontItem, control)}
          />
        </div>
        {firstStackedItem ? (
          <div ref={noticesRef} className="relative z-20 min-h-3" {...noticeEvents}>
            <ComposerBanner.Peek
              ref={peekRef}
              variant={firstStackedItem.variant}
              aria-label="Show other notices"
              aria-expanded={stackExpanded}
              aria-controls={expandedItemsId}
              aria-hidden={stackExpanded}
              tabIndex={stackExpanded ? -1 : 0}
              onClick={(event) => {
                event.currentTarget.focus({ preventScroll: true });
                pendingFocusRef.current = "notice";
                setStackExpanded(true);
              }}
              data-expanded={stackExpanded}
              className="data-[expanded=true]:pointer-events-none data-[expanded=true]:invisible data-[expanded=true]:opacity-0"
            />
            <ExpandedNotices
              expandedItemsId={expandedItemsId}
              expandedItemsRef={expandedItemsRef}
              stackExpanded={stackExpanded}
              stackedItems={stackedItems}
              onDismissRequest={requestNoticeDismissal}
            />
          </div>
        ) : null}
      </div>
    </ComposerBanner.Attachment>
  );
}

function useNoticeFocus(
  stackExpanded: boolean,
  pendingFocusRef: RefObject<"peek" | "notice" | null>,
  expandedItemsRef: RefObject<HTMLDivElement | null>,
  peekRef: RefObject<HTMLButtonElement | null>,
) {
  useLayoutEffect(() => {
    if (stackExpanded && pendingFocusRef.current === "notice") {
      pendingFocusRef.current = null;
      const firstControl = Array.from(
        expandedItemsRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]',
        ) ?? [],
      ).find(
        (control) =>
          control.getClientRects().length > 0 && getComputedStyle(control).visibility !== "hidden",
      );
      (firstControl ?? expandedItemsRef.current)?.focus({ preventScroll: true });
    } else if (!stackExpanded && pendingFocusRef.current === "peek") {
      pendingFocusRef.current = null;
      peekRef.current?.focus({ preventScroll: true });
    }
  }, [stackExpanded, pendingFocusRef, expandedItemsRef, peekRef]);
}

function useNoticeEvents(
  stackExpanded: boolean,
  setStackExpanded: (expanded: boolean) => void,
  peekRef: RefObject<HTMLButtonElement | null>,
  pendingFocusRef: RefObject<"peek" | "notice" | null>,
): HTMLAttributes<HTMLDivElement> {
  return {
    onPointerEnter: (event) => {
      if (event.pointerType === "touch") return;
      if (document.activeElement === peekRef.current) {
        pendingFocusRef.current = "notice";
      }
      setStackExpanded(true);
    },
    onPointerLeave: (event) => {
      if (!event.currentTarget.contains(document.activeElement)) setStackExpanded(false);
    },
    onBlurCapture: (event) => {
      if (
        !event.currentTarget.contains(event.relatedTarget) &&
        !event.currentTarget.matches(":hover")
      ) {
        setStackExpanded(false);
      }
    },
    onKeyDown: (event) => {
      if (event.key !== "Escape" || !stackExpanded) return;
      event.preventDefault();
      event.stopPropagation();
      pendingFocusRef.current = "peek";
      setStackExpanded(false);
    },
  };
}

function ExpandedNotices({
  expandedItemsId,
  expandedItemsRef,
  stackExpanded,
  stackedItems,
  onDismissRequest,
}: {
  readonly expandedItemsId: string;
  readonly expandedItemsRef: RefObject<HTMLDivElement | null>;
  readonly stackExpanded: boolean;
  readonly stackedItems: readonly ComposerBannerStackItem[];
  readonly onDismissRequest: (item: ComposerBannerStackItem, control: HTMLButtonElement) => void;
}) {
  return (
    <div
      id={expandedItemsId}
      ref={expandedItemsRef}
      role="group"
      aria-label="Other notices"
      tabIndex={-1}
      data-composer-banner-stack-expanded-items="true"
      className={cn(
        "grid transition-[grid-template-rows] duration-150 ease-out focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        stackExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={cn(
            "transform-gpu space-y-2 pb-2 transition-[opacity,transform] duration-150 ease-out will-change-[opacity,transform]",
            stackExpanded
              ? "pointer-events-auto visible translate-y-0 opacity-100"
              : "pointer-events-none invisible translate-y-1 opacity-0",
          )}
        >
          {stackedItems.map((item) => (
            <div key={item.id}>
              <ComposerBannerStackAlert
                item={item}
                attached={false}
                onDismissRequest={(control) => onDismissRequest(item, control)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function requestNoticeDismissal(item: ComposerBannerStackItem, control: HTMLButtonElement): void {
  const composer = control
    .closest("[data-composer-banner-drawer]")
    ?.parentElement?.querySelector<HTMLElement>('textarea, [contenteditable="true"]');
  composer?.focus({ preventScroll: true });
  item.onDismiss?.();
}

function blurNoticeFocus(notices: HTMLDivElement | null) {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && notices?.contains(activeElement))
    activeElement.blur();
}
